import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import Layout from '../components/layout/Layout';
import Button from '../components/common/Button';
import PageHeader, { KpiTile, SectionCard, Spinner } from '../components/common/PageHeader';
import { NativeSelect } from '../components/common/FormField';
import { useToast } from '../contexts/ToastContext';
import { queryKeys } from '../lib/queryClient';
import { api, errorMessage, triggerBlobDownload } from '../services/api';
import { financialYear, fmtCurrency, recentFinancialYears } from '../utils/format';

interface Pnl {
  fy: string;
  from: string;
  to: string;
  months: { month: string; label: string; revenue: number; expenses: number; profit: number }[];
  categories: { categoryId: number; name: string; months: Record<string, number>; total: number }[];
  totals: { revenue: number; expenses: number; profit: number };
}

const money = (n: number) => (n ? fmtCurrency(n, 0).replace('₹', '') : '—');

export default function ReportsPage() {
  const { showToast } = useToast();
  const [fy, setFy] = useState(financialYear());
  const [exporting, setExporting] = useState<string | null>(null);

  const { data: pnl, isLoading } = useQuery({
    queryKey: queryKeys.reports.pnl(fy),
    queryFn: () => api.get<Pnl>('/reports/pnl', { fy }),
  });

  async function exportFile(type: 'expenses' | 'invoices') {
    if (!pnl) return;
    setExporting(type);
    try {
      const blob = await api.download(`/reports/export/${type}`, { from: pnl.from, to: pnl.to });
      triggerBlobDownload(blob, `${type}_FY${fy}.xlsx`);
    } catch (err) {
      showToast(errorMessage(err, 'Export failed'), 'error');
    } finally {
      setExporting(null);
    }
  }

  const th = 'px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider text-ink-500 whitespace-nowrap';
  const td = 'px-3 py-2 text-right font-mono text-[13px] whitespace-nowrap';

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto">
        <PageHeader
          title="Reports"
          subtitle="Profit & loss by month for the financial year (April – March)"
          actions={
            <>
              <NativeSelect className="w-full sm:w-40" value={fy} onChange={setFy}
                options={recentFinancialYears().map((y) => ({ value: y, label: `FY ${y}` }))} />
              <Button variant="outline" icon={exporting === 'invoices' ? 'fa-spinner fa-spin' : 'fa-file-excel'} disabled={!!exporting} onClick={() => exportFile('invoices')}>
                Invoices
              </Button>
              <Button variant="outline" icon={exporting === 'expenses' ? 'fa-spinner fa-spin' : 'fa-file-excel'} disabled={!!exporting} onClick={() => exportFile('expenses')}>
                Expenses
              </Button>
            </>
          }
        />

        {isLoading || !pnl ? (
          <Spinner />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 lg:mb-6">
              <KpiTile label="Revenue (excl. GST)" value={fmtCurrency(pnl.totals.revenue, 0)} icon="fa-arrow-trend-up" tone="brand" />
              <KpiTile label="Expenses" value={fmtCurrency(pnl.totals.expenses, 0)} icon="fa-wallet" tone="accent" />
              <KpiTile label={pnl.totals.profit >= 0 ? 'Net Profit' : 'Net Loss'} value={fmtCurrency(Math.abs(pnl.totals.profit), 0)}
                icon="fa-scale-balanced" tone={pnl.totals.profit >= 0 ? 'green' : 'red'} />
            </div>

            <SectionCard title={`Profit & Loss — FY ${pnl.fy} (₹)`} bodyClassName="overflow-x-auto">
              <table className="w-full min-w-[1100px] text-sm">
                <thead>
                  <tr className="border-b border-ink-200 bg-white">
                    <th className={`${th} text-left sticky left-0 bg-white`}>Particulars</th>
                    {pnl.months.map((m) => <th key={m.month} className={th}>{m.label}</th>)}
                    <th className={`${th} text-ink-900`}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-ink-200 bg-brand-50/60">
                    <td className="px-3 py-2 font-bold text-ink-900 sticky left-0 bg-brand-50">Revenue</td>
                    {pnl.months.map((m) => <td key={m.month} className={`${td} font-semibold text-ink-900`}>{money(m.revenue)}</td>)}
                    <td className={`${td} font-bold text-ink-900`}>{money(pnl.totals.revenue)}</td>
                  </tr>
                  {pnl.categories.map((c) => (
                    <tr key={c.categoryId} className="border-b border-ink-100 hover:bg-ink-50">
                      <td className="px-3 py-2 pl-6 text-ink-700 sticky left-0 bg-white whitespace-nowrap">{c.name}</td>
                      {pnl.months.map((m) => <td key={m.month} className={`${td} text-ink-600`}>{money(c.months[m.month] ?? 0)}</td>)}
                      <td className={`${td} font-semibold text-ink-800`}>{money(c.total)}</td>
                    </tr>
                  ))}
                  {pnl.categories.length === 0 && (
                    <tr><td colSpan={14} className="px-3 py-6 text-center text-ink-400">No expenses recorded in this year.</td></tr>
                  )}
                  <tr className="border-y border-ink-200 bg-accent-50/60">
                    <td className="px-3 py-2 font-bold text-ink-900 sticky left-0 bg-accent-50">Total Expenses</td>
                    {pnl.months.map((m) => <td key={m.month} className={`${td} font-semibold text-ink-900`}>{money(m.expenses)}</td>)}
                    <td className={`${td} font-bold text-ink-900`}>{money(pnl.totals.expenses)}</td>
                  </tr>
                  <tr className="bg-ink-50">
                    <td className="px-3 py-2.5 font-extrabold text-ink-900 sticky left-0 bg-ink-50">Net Profit / (Loss)</td>
                    {pnl.months.map((m) => (
                      <td key={m.month} className={`${td} font-bold ${m.profit < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                        {m.revenue || m.expenses ? (m.profit < 0 ? `(${money(-m.profit)})` : money(m.profit)) : '—'}
                      </td>
                    ))}
                    <td className={`${td} font-extrabold ${pnl.totals.profit < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                      {pnl.totals.profit < 0 ? `(${money(-pnl.totals.profit)})` : money(pnl.totals.profit)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </SectionCard>

            <p className="text-xs text-ink-500 mt-3">
              Revenue is the taxable value of non-cancelled invoices by invoice date (GST excluded — it is payable to the
              government). Expenses are counted at the amount paid, including any GST on the bill. Capital entries are not
              part of profit &amp; loss. Share the Excel exports with your CA for filing.
            </p>
          </>
        )}
      </div>
    </Layout>
  );
}
